import { formDocSchema } from "./document.js";
import { isInputField } from "./fields.js";
import { newChoiceId, newFieldId } from "./ids.js";
export class NotPublishableError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = "NotPublishableError";
    }
}
/**
 * The document schema deliberately allows an empty `fields` array so autosave of
 * a brand new form does not 400. Publish is where we insist on real content.
 */
export const assertPublishable = (doc) => {
    const parsed = formDocSchema.safeParse(doc);
    if (!parsed.success) {
        throw new NotPublishableError("invalid_document", "This form has invalid settings.");
    }
    if (!parsed.data.title.trim()) {
        throw new NotPublishableError("empty_title", "Give your form a title.");
    }
    if (parsed.data.fields.length === 0) {
        throw new NotPublishableError("no_fields", "Add at least one question before publishing.");
    }
    if (!parsed.data.fields.some(isInputField)) {
        throw new NotPublishableError("no_input_fields", "Add at least one question people can answer.");
    }
    // Blank choice labels are legal in a draft (you type into a new option
    // after adding it) but never in a live form: an unlabelled radio button
    // is unanswerable, and its stored answer would be unreadable afterwards.
    for (const field of parsed.data.fields) {
        if (!("choices" in field))
            continue;
        if (field.choices.some((choice) => choice.label.trim() === "")) {
            throw new NotPublishableError("empty_choice", `Every option in “${field.label}” needs a label.`);
        }
    }
};
/**
 * Stable stringify: key order must not affect the hash, or reordering a
 * settings object in the builder would look like a content change and mint a
 * pointless new version.
 */
export const canonicalJson = (value) => {
    const walk = (v) => {
        if (Array.isArray(v))
            return v.map(walk);
        if (v && typeof v === "object") {
            return Object.fromEntries(Object.keys(v)
                .sort()
                .map((k) => [k, walk(v[k])]));
        }
        return v;
    };
    return JSON.stringify(walk(value));
};
/**
 * Which in-place type changes keep existing answers representable.
 *
 * A change that is NOT lossless must mint a new field id in the builder — the
 * old field then becomes a `removed` column rather than a field whose stored
 * answers no longer match its declared type. This is the single most important
 * builder rule: it turns an impossible rendering problem into a solved one.
 */
const LOSSLESS_TARGETS = {
    short_text: ["long_text"],
    long_text: ["short_text"],
    email: ["short_text", "long_text"],
    date: ["short_text", "long_text"],
    rating: ["number"],
    multiple_choice: ["dropdown"],
    dropdown: ["multiple_choice"],
};
export const isLosslessTypeChange = (from, to) => from === to || (LOSSLESS_TARGETS[from]?.includes(to) ?? false);
/**
 * A copy of a document with every field and choice id re-minted.
 *
 * Duplicating a form must not duplicate its ids. Two forms sharing field ids
 * is a latent data-integrity bug — the copies drift apart while anything
 * keying off field id across forms silently conflates them — and it breaks
 * the rule the whole response model rests on: an id identifies exactly one
 * question, forever.
 */
export const withFreshIds = (doc, title) => ({
    ...doc,
    ...(title === undefined ? {} : { title }),
    fields: doc.fields.map((field) => ({
        ...field,
        id: newFieldId(),
        ...("choices" in field
            ? {
                choices: field.choices.map((choice) => ({
                    ...choice,
                    id: newChoiceId(),
                })),
            }
            : {}),
    })),
});
//# sourceMappingURL=publish.js.map